import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminManualInvoices.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

type ManualInvoiceStatus = "draft" | "sent" | "paid" | "canceled";

interface UserSearchResult {
  user_id: string;
  email: string;
  full_name: string;
  created_at?: string | null;
}

interface LineItemForm {
  id: string;
  description: string;
  quantity: string;
  unit_label: string;
  unit_amount_sar: string;
}

interface PreviewLineItem {
  sort_order: number;
  description: string;
  quantity: string;
  unit_label?: string | null;
  unit_amount_halala: number;
  line_total_halala: number;
  unit_amount_sar: string;
}

interface PreviewResponse {
  recipient: {
    name: string;
    email: string;
    company_name?: string | null;
    phone?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
    vat_number?: string | null;
  };
  linked_user?: UserSearchResult | null;
  matched_user?: UserSearchResult | null;
  issue_date: string;
  due_date?: string | null;
  notes?: string | null;
  payment_instructions?: string | null;
  internal_notes?: string | null;
  line_items: PreviewLineItem[];
  pricing: {
    subtotal_amount_halala: number;
    tax_percentage: number;
    tax_amount_halala: number;
    total_amount_halala: number;
    currency: string;
  };
  can_create: boolean;
}

interface ManualInvoiceLineItem {
  id: string;
  description: string;
  quantity: string;
  unit_label?: string | null;
  unit_amount_halala: number;
  unit_amount_sar: string;
  line_total_halala: number;
}

interface ManualInvoice {
  id: string;
  admin_user_id: string;
  linked_user_id?: string | null;
  invoice_number: string;
  recipient_name: string;
  recipient_email: string;
  recipient_company_name?: string | null;
  recipient_phone?: string | null;
  recipient_address_line1?: string | null;
  recipient_address_line2?: string | null;
  recipient_city?: string | null;
  recipient_state?: string | null;
  recipient_postal_code?: string | null;
  recipient_country?: string | null;
  recipient_vat_number?: string | null;
  currency: string;
  subtotal_amount_halala: number;
  tax_percentage: number;
  tax_amount_halala: number;
  total_amount_halala: number;
  status: ManualInvoiceStatus;
  issue_date: string;
  due_date?: string | null;
  notes?: string | null;
  payment_instructions?: string | null;
  internal_notes?: string | null;
  payment_reference?: string | null;
  pdf_path?: string | null;
  invoice_email_sent_at?: string | null;
  invoice_email_send_count: number;
  sent_at?: string | null;
  paid_at?: string | null;
  canceled_at?: string | null;
  last_error?: string | null;
  created_at: string;
  linked_user?: UserSearchResult | null;
  is_overdue?: boolean;
  line_items: ManualInvoiceLineItem[];
}

const label: React.CSSProperties = {
  display: "block",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "var(--Colors-Primary-800)",
  marginBottom: "0.45rem",
};

const statusBadge = (
  status: ManualInvoiceStatus | "overdue"
): React.CSSProperties => {
  const palette: Record<string, { bg: string; color: string }> = {
    draft: { bg: "var(--theme-surface-soft)", color: "var(--theme-muted)" },
    sent: { bg: "var(--theme-warning-bg)", color: "var(--theme-warning-text)" },
    paid: { bg: "var(--theme-success-bg)", color: "var(--theme-success-text)" },
    canceled: { bg: "var(--theme-danger-bg)", color: "var(--theme-danger-text)" },
    overdue: { bg: "var(--theme-warning-bg)", color: "var(--theme-warning-text)" },
  };
  const style = palette[status] || palette.draft;
  return {
    display: "inline-block",
    padding: "0.25rem 0.65rem",
    borderRadius: "999px",
    background: style.bg,
    color: style.color,
    fontWeight: 800,
    fontSize: "0.75rem",
    textTransform: "capitalize",
  };
};

const defaultIssueDate = () => new Date().toISOString().slice(0, 10);

const defaultDueDate = () => {
  const next = new Date();
  next.setDate(next.getDate() + 14);
  return next.toISOString().slice(0, 10);
};

const createLineItem = (): LineItemForm => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  description: "",
  quantity: "1",
  unit_label: "",
  unit_amount_sar: "",
});

const formatAmount = (halala: number) => `${(halala / 100).toFixed(2)} SAR`;

const parseMoneyInputToHalala = (value: string) => {
  const number = Number.parseFloat(value || "0");
  if (Number.isNaN(number) || number < 0) return 0;
  return Math.round(number * 100);
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: value.includes("T") ? "numeric" : undefined,
      minute: value.includes("T") ? "2-digit" : undefined,
    });
  } catch {
    return value;
  }
};

const normalizeErrorMessage = (value: unknown, fallback: string) => {
  if (value instanceof Error) return value.message || fallback;
  if (typeof value === "string") return value || fallback;
  if (value && typeof value === "object") {
    const payload = value as Record<string, unknown>;
    if (typeof payload.error === "string") return payload.error;
    if (typeof payload.message === "string") return payload.message;
  }
  return fallback;
};

const readApiError = (payload: any, fallback: string) =>
  normalizeErrorMessage(payload?.error ?? payload?.message ?? payload, fallback);

const buildPreviewFromInvoice = (invoice: ManualInvoice): PreviewResponse => ({
  recipient: {
    name: invoice.recipient_name,
    email: invoice.recipient_email,
    company_name: invoice.recipient_company_name,
    phone: invoice.recipient_phone,
    address_line1: invoice.recipient_address_line1,
    address_line2: invoice.recipient_address_line2,
    city: invoice.recipient_city,
    state: invoice.recipient_state,
    postal_code: invoice.recipient_postal_code,
    country: invoice.recipient_country,
    vat_number: invoice.recipient_vat_number,
  },
  linked_user: invoice.linked_user || null,
  matched_user: null,
  issue_date: invoice.issue_date,
  due_date: invoice.due_date,
  notes: invoice.notes,
  payment_instructions: invoice.payment_instructions,
  internal_notes: invoice.internal_notes,
  line_items: invoice.line_items.map((item, index) => ({
    sort_order: index,
    description: item.description,
    quantity: item.quantity,
    unit_label: item.unit_label,
    unit_amount_halala: item.unit_amount_halala,
    unit_amount_sar: item.unit_amount_sar,
    line_total_halala: item.line_total_halala,
  })),
  pricing: {
    subtotal_amount_halala: invoice.subtotal_amount_halala,
    tax_percentage: invoice.tax_percentage,
    tax_amount_halala: invoice.tax_amount_halala,
    total_amount_halala: invoice.total_amount_halala,
    currency: invoice.currency,
  },
  can_create: true,
});

const AdminManualInvoices: React.FC = () => {
  const { user } = useAuthContext();

  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [linkedUser, setLinkedUser] = useState<UserSearchResult | null>(null);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientCompanyName, setRecipientCompanyName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientAddressLine1, setRecipientAddressLine1] = useState("");
  const [recipientAddressLine2, setRecipientAddressLine2] = useState("");
  const [recipientCity, setRecipientCity] = useState("");
  const [recipientState, setRecipientState] = useState("");
  const [recipientPostalCode, setRecipientPostalCode] = useState("");
  const [recipientCountry, setRecipientCountry] = useState("Saudi Arabia");
  const [recipientVatNumber, setRecipientVatNumber] = useState("");
  const [issueDate, setIssueDate] = useState(defaultIssueDate());
  const [dueDate, setDueDate] = useState(defaultDueDate());
  const [taxPercentage, setTaxPercentage] = useState("0");
  const [notes, setNotes] = useState("");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [lineItems, setLineItems] = useState<LineItemForm[]>([createLineItem()]);

  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<ManualInvoice[]>([]);
  const [paymentReferences, setPaymentReferences] = useState<Record<string, string>>(
    {}
  );
  const [paymentDates, setPaymentDates] = useState<Record<string, string>>({});

  const resetComposer = useCallback(() => {
    setEditingInvoiceId(null);
    setLinkedUser(null);
    setRecipientName("");
    setRecipientEmail("");
    setRecipientCompanyName("");
    setRecipientPhone("");
    setRecipientAddressLine1("");
    setRecipientAddressLine2("");
    setRecipientCity("");
    setRecipientState("");
    setRecipientPostalCode("");
    setRecipientCountry("Saudi Arabia");
    setRecipientVatNumber("");
    setIssueDate(defaultIssueDate());
    setDueDate(defaultDueDate());
    setTaxPercentage("0");
    setNotes("");
    setPaymentInstructions("");
    setInternalNotes("");
    setLineItems([createLineItem()]);
    setPreview(null);
    setPreviewModalOpen(false);
    setUserSearch("");
    setSearchResults([]);
    setSearchError(null);
  }, []);

  const fetchInvoices = useCallback(async () => {
    if (!user?.id) return;
    setListLoading(true);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/manual-invoices?user_id=${user.id}`
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, "Failed to load manual invoices"));
      }
      setInvoices(Array.isArray(data) ? data : []);
    } catch (error: any) {
      (window as any).showNotification?.(
        normalizeErrorMessage(error, "Failed to load manual invoices"),
        "error"
      );
    } finally {
      setListLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    setPreview(null);
    setPreviewModalOpen(false);
  }, [
    editingInvoiceId,
    linkedUser,
    recipientName,
    recipientEmail,
    recipientCompanyName,
    recipientPhone,
    recipientAddressLine1,
    recipientAddressLine2,
    recipientCity,
    recipientState,
    recipientPostalCode,
    recipientCountry,
    recipientVatNumber,
    issueDate,
    dueDate,
    taxPercentage,
    notes,
    paymentInstructions,
    internalNotes,
    lineItems,
  ]);

  useEffect(() => {
    if (!previewModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPreviewModalOpen(false);
      }
    };
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewModalOpen]);

  useEffect(() => {
    if (!user?.id) return;
    const query = userSearch.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await authenticatedFetch(
          `${apiUrl}/api/admin/users/search?user_id=${user.id}&query=${encodeURIComponent(
            query
          )}&limit=10`
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(readApiError(data, "Failed to search users"));
        }
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (error: any) {
        setSearchResults([]);
        setSearchError(normalizeErrorMessage(error, "Failed to search users"));
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [userSearch, user?.id]);

  const buildPayload = () => ({
    linked_user_id: linkedUser?.user_id || null,
    recipient_name: recipientName,
    recipient_email: recipientEmail,
    recipient_company_name: recipientCompanyName,
    recipient_phone: recipientPhone,
    recipient_address_line1: recipientAddressLine1,
    recipient_address_line2: recipientAddressLine2,
    recipient_city: recipientCity,
    recipient_state: recipientState,
    recipient_postal_code: recipientPostalCode,
    recipient_country: recipientCountry,
    recipient_vat_number: recipientVatNumber,
    issue_date: issueDate,
    due_date: dueDate || null,
    tax_percentage: taxPercentage || "0",
    notes,
    payment_instructions: paymentInstructions,
    internal_notes: internalNotes,
    line_items: lineItems.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      unit_label: item.unit_label,
      unit_amount_sar: item.unit_amount_sar,
    })),
  });

  const handlePreview = async () => {
    if (!user?.id) return;
    setPreviewLoading(true);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/manual-invoices/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ admin_id: user.id, ...buildPayload() }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, "Failed to build preview"));
      }
      setPreview(data);
      setPreviewModalOpen(true);
      (window as any).showNotification?.("Preview updated", "success");
    } catch (error: any) {
      (window as any).showNotification?.(
        normalizeErrorMessage(error, "Failed to build preview"),
        "error"
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const saveDraft = async (sendAfterSave = false) => {
    if (!user?.id) return;
    setSubmitting(true);
    try {
      const endpoint = editingInvoiceId
        ? `${apiUrl}/api/admin/manual-invoices/${editingInvoiceId}`
        : `${apiUrl}/api/admin/manual-invoices`;
      const method = editingInvoiceId ? "PUT" : "POST";

      const res = await authenticatedFetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ admin_id: user.id, ...buildPayload() }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          readApiError(
            data,
            editingInvoiceId ? "Failed to update draft" : "Failed to save draft"
          )
        );
      }

      let finalInvoice = data as ManualInvoice;
      if (sendAfterSave) {
        const sendRes = await authenticatedFetch(
          `${apiUrl}/api/admin/manual-invoices/${data.id}/send`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ admin_id: user.id }),
          }
        );
        const sendData = await sendRes.json();
        if (!sendRes.ok) {
          await fetchInvoices();
          throw new Error(readApiError(sendData, "Draft saved, but sending failed"));
        }
        finalInvoice = sendData as ManualInvoice;
      }

      await fetchInvoices();
      if (sendAfterSave) {
        (window as any).showNotification?.("Invoice sent", "success");
        resetComposer();
      } else {
        setEditingInvoiceId(finalInvoice.id);
        setPreview(buildPreviewFromInvoice(finalInvoice));
        (window as any).showNotification?.("Draft saved", "success");
      }
    } catch (error: any) {
      (window as any).showNotification?.(
        normalizeErrorMessage(error, "Failed to save invoice"),
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const loadDraft = (invoice: ManualInvoice) => {
    setEditingInvoiceId(invoice.id);
    setLinkedUser(invoice.linked_user || null);
    setRecipientName(invoice.recipient_name || "");
    setRecipientEmail(invoice.recipient_email || "");
    setRecipientCompanyName(invoice.recipient_company_name || "");
    setRecipientPhone(invoice.recipient_phone || "");
    setRecipientAddressLine1(invoice.recipient_address_line1 || "");
    setRecipientAddressLine2(invoice.recipient_address_line2 || "");
    setRecipientCity(invoice.recipient_city || "");
    setRecipientState(invoice.recipient_state || "");
    setRecipientPostalCode(invoice.recipient_postal_code || "");
    setRecipientCountry(invoice.recipient_country || "Saudi Arabia");
    setRecipientVatNumber(invoice.recipient_vat_number || "");
    setIssueDate(invoice.issue_date || defaultIssueDate());
    setDueDate(invoice.due_date || "");
    setTaxPercentage(String(invoice.tax_percentage ?? 0));
    setNotes(invoice.notes || "");
    setPaymentInstructions(invoice.payment_instructions || "");
    setInternalNotes(invoice.internal_notes || "");
    setLineItems(
      invoice.line_items.length
        ? invoice.line_items.map((item) => ({
            id: item.id,
            description: item.description,
            quantity: item.quantity,
            unit_label: item.unit_label || "",
            unit_amount_sar: item.unit_amount_sar,
          }))
        : [createLineItem()]
    );
    setPreview(buildPreviewFromInvoice(invoice));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const runInvoiceAction = async (
    invoiceId: string,
    action: "send" | "resend" | "cancel"
  ) => {
    if (!user?.id) return;
    setActionLoading(`${invoiceId}:${action}`);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/manual-invoices/${invoiceId}/${action}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ admin_id: user.id }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, `Failed to ${action} invoice`));
      }
      await fetchInvoices();
      (window as any).showNotification?.(`Invoice ${action}ed`, "success");
    } catch (error: any) {
      (window as any).showNotification?.(
        normalizeErrorMessage(error, `Failed to ${action} invoice`),
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const markPaid = async (invoiceId: string) => {
    if (!user?.id) return;
    setActionLoading(`${invoiceId}:mark-paid`);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/manual-invoices/${invoiceId}/mark-paid`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            admin_id: user.id,
            payment_reference: paymentReferences[invoiceId] || "",
            paid_at: paymentDates[invoiceId] || "",
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, "Failed to mark invoice as paid"));
      }
      await fetchInvoices();
      (window as any).showNotification?.("Invoice marked as paid", "success");
    } catch (error: any) {
      (window as any).showNotification?.(
        normalizeErrorMessage(error, "Failed to mark invoice as paid"),
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const openPdf = async (invoiceId: string) => {
    if (!user?.id) return;
    setActionLoading(`${invoiceId}:pdf`);
    try {
      const res = await authenticatedFetch(
        `${apiUrl}/api/admin/manual-invoices/${invoiceId}/pdf?user_id=${user.id}`
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(readApiError(data, "Failed to open PDF"));
      }
      if (data.signed_url) {
        window.open(data.signed_url, "_blank", "noopener,noreferrer");
      }
    } catch (error: any) {
      (window as any).showNotification?.(
        normalizeErrorMessage(error, "Failed to open PDF"),
        "error"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const addLineItem = () => {
    setLineItems((current) => [...current, createLineItem()]);
  };

  const updateLineItem = (
    id: string,
    field: keyof Omit<LineItemForm, "id">,
    value: string
  ) => {
    setLineItems((current) =>
      current.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeLineItem = (id: string) => {
    setLineItems((current) =>
      current.length === 1 ? current : current.filter((item) => item.id !== id)
    );
  };

  const filteredSearchResults = useMemo(
    () =>
      searchResults.filter(
        (candidate) => candidate.user_id !== linkedUser?.user_id
      ),
    [searchResults, linkedUser?.user_id]
  );

  const chooseLinkedUser = (candidate: UserSearchResult) => {
    setLinkedUser(candidate);
    if (!recipientEmail) setRecipientEmail(candidate.email);
    if (!recipientName) setRecipientName(candidate.full_name || candidate.email);
    setUserSearch("");
    setSearchResults([]);
    setSearchError(null);
  };

  const composerSnapshot = useMemo(() => {
    const subtotalHalala = lineItems.reduce((sum, item) => {
      const unitAmount = parseMoneyInputToHalala(item.unit_amount_sar);
      const quantityValue = Number.parseFloat(item.quantity || "0");
      const quantity =
        Number.isNaN(quantityValue) || quantityValue <= 0 ? 0 : quantityValue;
      return sum + Math.round(unitAmount * quantity);
    }, 0);
    const parsedTax = Number.parseFloat(taxPercentage || "0");
    const safeTaxRate = Number.isNaN(parsedTax) || parsedTax < 0 ? 0 : parsedTax;
    const taxHalala = Math.round(subtotalHalala * (safeTaxRate / 100));
    const filledItemCount = lineItems.filter(
      (item) => item.description.trim() || item.unit_amount_sar.trim()
    ).length;

    return {
      lineItemCount: filledItemCount || lineItems.length,
      subtotalHalala,
      taxHalala,
      totalHalala: subtotalHalala + taxHalala,
      recipientLabel: recipientName || recipientEmail || "Unassigned recipient",
    };
  }, [lineItems, recipientEmail, recipientName, taxPercentage]);

  const dashboardStats = useMemo(() => {
    const draftInvoices = invoices.filter((invoice) => invoice.status === "draft");
    const sentInvoices = invoices.filter((invoice) => invoice.status === "sent");
    const paidInvoices = invoices.filter((invoice) => invoice.status === "paid");
    const overdueInvoices = invoices.filter((invoice) => invoice.is_overdue);

    return {
      draftCount: draftInvoices.length,
      draftTotalHalala: draftInvoices.reduce(
        (sum, invoice) => sum + invoice.total_amount_halala,
        0
      ),
      sentCount: sentInvoices.length,
      sentTotalHalala: sentInvoices.reduce(
        (sum, invoice) => sum + invoice.total_amount_halala,
        0
      ),
      paidCount: paidInvoices.length,
      paidTotalHalala: paidInvoices.reduce(
        (sum, invoice) => sum + invoice.total_amount_halala,
        0
      ),
      overdueCount: overdueInvoices.length,
    };
  }, [invoices]);

  const composerModeLabel = editingInvoiceId ? "Editing draft" : "New draft";
  const liveInvoiceTotal = preview?.pricing.total_amount_halala ?? composerSnapshot.totalHalala;
  const timelineSummary = dueDate ? `Due ${formatDate(dueDate)}` : "No due date set";
  const awaitingPaymentMeta = dashboardStats.overdueCount
    ? `${dashboardStats.sentCount} sent invoices · ${dashboardStats.overdueCount} overdue`
    : dashboardStats.sentCount
      ? `${dashboardStats.sentCount} sent invoices awaiting manual settlement`
      : "Nothing is currently waiting for manual settlement";

  return (
    <div className={styles.pageShell}>
      <section className={styles.hero}>
        <div className={styles.heroHeader}>
          <div className={styles.heroCopy}>
            <div className={styles.heroEyebrow}>Finance Workspace</div>
            <h2 className={styles.heroTitle}>Manual Invoices</h2>
            <p className={styles.heroText}>
              Build branded offline invoices that still feel native to the
              product. Compose professional billing records, add structured SAR
              line items, send polished PDFs, and manually close payment after
              transfer or cash collection.
            </p>
          </div>
          <div className={styles.heroActions}>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.actionButtonGhost}`}
              onClick={fetchInvoices}
              disabled={listLoading}
            >
              {listLoading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
              onClick={resetComposer}
            >
              {editingInvoiceId ? "New Invoice" : "Reset Composer"}
            </button>
          </div>
        </div>

        <div className={styles.heroMetrics}>
          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>Live Invoice Value</div>
            <div className={styles.metricValue}>{formatAmount(liveInvoiceTotal)}</div>
            <div className={styles.metricMeta}>
              {composerSnapshot.recipientLabel} · {composerSnapshot.lineItemCount}{" "}
              line items in the current composer
            </div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>Draft Pipeline</div>
            <div className={styles.metricValue}>{dashboardStats.draftCount}</div>
            <div className={styles.metricMeta}>
              {dashboardStats.draftCount
                ? `${formatAmount(dashboardStats.draftTotalHalala)} saved across editable drafts`
                : "No drafts are waiting in the pipeline"}
            </div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>Awaiting Payment</div>
            <div className={styles.metricValue}>
              {formatAmount(dashboardStats.sentTotalHalala)}
            </div>
            <div className={styles.metricMeta}>{awaitingPaymentMeta}</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricLabel}>Collected</div>
            <div className={styles.metricValue}>
              {formatAmount(dashboardStats.paidTotalHalala)}
            </div>
            <div className={styles.metricMeta}>
              {dashboardStats.paidCount
                ? `${dashboardStats.paidCount} invoices closed manually`
                : "No manual payments have been marked yet"}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.mainSurface}>
        <div className={styles.surfaceHeader}>
          <div className={styles.surfaceTitleWrap}>
            <div className={styles.surfaceEyebrow}>Composer</div>
            <h3 className={styles.surfaceTitle}>
              {editingInvoiceId ? "Refine Draft Before Sending" : "Build a New Invoice"}
            </h3>
            <p className={styles.surfaceText}>
              Configure dates, capture a professional billing profile, and keep
              the invoice editable until it is ready to send.
            </p>
          </div>
          <span className={styles.pill} style={statusBadge("draft")}>
            {composerModeLabel}
          </span>
        </div>

        <div className={styles.topSummaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Mode</div>
            <div className={styles.summaryValue}>{composerModeLabel}</div>
            <div className={styles.summaryHint}>
              Drafts remain editable until they are sent or canceled.
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Recipient</div>
            <div className={styles.summaryValue}>{composerSnapshot.recipientLabel}</div>
            <div className={styles.summaryHint}>
              {linkedUser
                ? `Linked to ${linkedUser.email}`
                : "Not linked to a platform user"}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Pricing</div>
            <div className={styles.summaryValue}>
              {composerSnapshot.lineItemCount} line items
            </div>
            <div className={styles.summaryHint}>
              {formatAmount(composerSnapshot.subtotalHalala)} subtotal ·{" "}
              {Number.parseFloat(taxPercentage || "0") || 0}% tax
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Timeline</div>
            <div className={styles.summaryValue}>{formatDate(issueDate)}</div>
            <div className={styles.summaryHint}>{timelineSummary}</div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.65rem",
            marginBottom: "0.75rem",
          }}
        >
          <div>
            <label style={label}>Invoice Date</label>
            <input
              className={styles.fieldInput}
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div>
            <label style={label}>Due Date</label>
            <input
              className={styles.fieldInput}
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
          <div>
            <label style={label}>Tax Percentage</label>
            <input
              className={styles.fieldInput}
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={taxPercentage}
              onChange={(e) => setTaxPercentage(e.target.value)}
            />
          </div>
          <div>
            <label style={label}>Currency</label>
            <div className={styles.summaryCard}>
              <div className={styles.summaryValue}>SAR</div>
              <div className={styles.summaryHint}>
                Manual invoices are currently issued in Saudi Riyals only.
              </div>
            </div>
          </div>
        </div>

        <div className={styles.splitGrid}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div>
                <h4 className={styles.panelTitle}>Optional Linked User</h4>
                <p className={styles.panelText}>
                  Associate the invoice with an existing C-Shaped account without
                  constraining who receives the billing email.
                </p>
              </div>
              <span className={styles.pill} style={statusBadge(linkedUser ? "paid" : "draft")}>
                {linkedUser ? "Linked" : "Optional"}
              </span>
            </div>

            <input
              className={styles.fieldInput}
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users by email or full name"
            />

            {(userSearch.trim().length >= 2 ||
              searchLoading ||
              filteredSearchResults.length > 0 ||
              !!searchError) && (
              <div className={styles.searchPanel} style={{ marginTop: "0.55rem" }}>
                {searchLoading ? (
                  <div className={styles.emptyState} style={{ border: "none", padding: "0.7rem" }}>
                    Searching users...
                  </div>
                ) : searchError ? (
                  <div className={styles.errorBox} style={{ marginTop: 0, padding: "0.7rem" }}>
                    {searchError}
                  </div>
                ) : filteredSearchResults.length === 0 ? (
                  <div className={styles.emptyState} style={{ border: "none", padding: "0.7rem" }}>
                    No matching users found.
                  </div>
                ) : (
                  filteredSearchResults.map((candidate) => (
                    <button
                      key={candidate.user_id}
                      type="button"
                      className={styles.searchResult}
                      onClick={() => chooseLinkedUser(candidate)}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "var(--Colors-Primary-800)",
                        }}
                      >
                        {candidate.full_name || "Unnamed user"}
                      </div>
                      <div className={styles.compactMeta} style={{ marginTop: "0.15rem" }}>
                        {candidate.email}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            <div style={{ marginTop: "0.65rem" }}>
              <label style={label}>Selected Link</label>
              <div className={styles.linkedUserBox} style={{ padding: "0.72rem 0.82rem" }}>
                {linkedUser ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "1rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "var(--Colors-Primary-800)",
                        }}
                      >
                        {linkedUser.full_name || "Unnamed user"}
                      </div>
                      <div className={styles.compactMeta}>{linkedUser.email}</div>
                    </div>
                    <button
                      type="button"
                      className={`${styles.actionButton} ${styles.actionButtonGhost}`}
                      onClick={() => setLinkedUser(null)}
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <span className={styles.compactMeta}>
                    No linked user selected. Search is optional and does not
                    control the billing recipient.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div>
                <h4 className={styles.panelTitle}>Recipient Details</h4>
                <p className={styles.panelText}>
                  Capture the exact company and contact fields you want printed on
                  the invoice PDF.
                </p>
              </div>
              <span
                className={styles.pill}
                style={{
                  background: "rgba(32, 91, 90, 0.08)",
                  color: "var(--Colors-Primary-700)",
                }}
              >
                Professional profile
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "0.65rem",
              }}
            >
              <div>
                <label style={label}>Recipient Name</label>
                <input
                  className={styles.fieldInput}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Primary billing contact"
                />
              </div>
              <div>
                <label style={label}>Recipient Email</label>
                <input
                  className={styles.fieldInput}
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="billing@example.com"
                />
              </div>
              <div>
                <label style={label}>Company Name</label>
                <input
                  className={styles.fieldInput}
                  value={recipientCompanyName}
                  onChange={(e) => setRecipientCompanyName(e.target.value)}
                  placeholder="Clinic or company name"
                />
              </div>
              <div>
                <label style={label}>Phone</label>
                <input
                  className={styles.fieldInput}
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="+966 ..."
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={label}>Address Line 1</label>
                <input
                  className={styles.fieldInput}
                  value={recipientAddressLine1}
                  onChange={(e) => setRecipientAddressLine1(e.target.value)}
                  placeholder="Street, building, district"
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={label}>Address Line 2</label>
                <input
                  className={styles.fieldInput}
                  value={recipientAddressLine2}
                  onChange={(e) => setRecipientAddressLine2(e.target.value)}
                  placeholder="Optional secondary address information"
                />
              </div>
              <div>
                <label style={label}>City</label>
                <input
                  className={styles.fieldInput}
                  value={recipientCity}
                  onChange={(e) => setRecipientCity(e.target.value)}
                />
              </div>
              <div>
                <label style={label}>State / Region</label>
                <input
                  className={styles.fieldInput}
                  value={recipientState}
                  onChange={(e) => setRecipientState(e.target.value)}
                />
              </div>
              <div>
                <label style={label}>Postal Code</label>
                <input
                  className={styles.fieldInput}
                  value={recipientPostalCode}
                  onChange={(e) => setRecipientPostalCode(e.target.value)}
                />
              </div>
              <div>
                <label style={label}>Country</label>
                <input
                  className={styles.fieldInput}
                  value={recipientCountry}
                  onChange={(e) => setRecipientCountry(e.target.value)}
                />
              </div>
              <div>
                <label style={label}>VAT / Tax ID</label>
                <input
                  className={styles.fieldInput}
                  value={recipientVatNumber}
                  onChange={(e) => setRecipientVatNumber(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.panelCard} ${styles.lineItemsSection}`} style={{ marginTop: "0.75rem" }}>
          <div className={styles.panelHeader}>
            <div>
              <h4 className={styles.panelTitle}>Line Items</h4>
              <p className={styles.panelText}>
                Enter each billable item in SAR. Totals update live above and in
                preview.
              </p>
            </div>
            <div className={styles.heroActions}>
              <span
                className={styles.pill}
                style={{
                  background: "rgba(32, 91, 90, 0.08)",
                  color: "var(--Colors-Primary-700)",
                }}
              >
                {formatAmount(composerSnapshot.totalHalala)}
              </span>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.actionButtonGhost}`}
                onClick={addLineItem}
              >
                Add Line Item
              </button>
            </div>
          </div>

          <div className={styles.lineItemsList}>
            {lineItems.map((item, index) => {
              const quantityValue = Number.parseFloat(item.quantity || "0");
              const quantity =
                Number.isNaN(quantityValue) || quantityValue < 0 ? 0 : quantityValue;
              const lineTotalHalala = Math.round(
                parseMoneyInputToHalala(item.unit_amount_sar) * quantity
              );

              return (
                <div key={item.id} className={styles.lineItemCard} style={{ padding: "0.78rem" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.75rem",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "flex", gap: "0.55rem", alignItems: "center" }}>
                      <span className={styles.lineItemIndex}>{index + 1}</span>
                      <div>
                        <div className={styles.panelTitle} style={{ fontSize: "0.98rem" }}>
                          {item.description || `Line item ${index + 1}`}
                        </div>
                        <div className={styles.compactMeta}>
                          Qty {item.quantity || "0"}
                          {item.unit_label ? ` ${item.unit_label}` : ""} ·{" "}
                          {item.unit_amount_sar || "0.00"} SAR per unit
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                      onClick={() => removeLineItem(item.id)}
                      disabled={lineItems.length === 1}
                    >
                      Remove
                    </button>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "0.55rem",
                      marginTop: "0.65rem",
                    }}
                  >
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={label}>Description</label>
                      <input
                        className={styles.fieldInput}
                        value={item.description}
                        onChange={(e) =>
                          updateLineItem(item.id, "description", e.target.value)
                        }
                        placeholder={`Line item ${index + 1}`}
                      />
                    </div>
                    <div>
                      <label style={label}>Quantity</label>
                      <input
                        className={styles.fieldInput}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) =>
                          updateLineItem(item.id, "quantity", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label style={label}>Unit Label</label>
                      <input
                        className={styles.fieldInput}
                        value={item.unit_label}
                        onChange={(e) =>
                          updateLineItem(item.id, "unit_label", e.target.value)
                        }
                        placeholder="seat / month / service"
                      />
                    </div>
                    <div>
                      <label style={label}>Unit Price (SAR)</label>
                      <input
                        className={styles.fieldInput}
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_amount_sar}
                        onChange={(e) =>
                          updateLineItem(item.id, "unit_amount_sar", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "0.65rem",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.75rem",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div className={styles.compactMeta}>
                      Decimal quantities are supported. Leave unit label blank if
                      the description already reads clearly.
                    </div>
                    <span
                      className={styles.pill}
                      style={{
                        background: "rgba(76, 192, 190, 0.14)",
                        color: "var(--Colors-Primary-800)",
                      }}
                    >
                      {formatAmount(lineTotalHalala)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.notesGrid} style={{ marginTop: "0.75rem" }}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div>
                <h4 className={styles.panelTitle}>Notes</h4>
                <p className={styles.panelText}>
                  Visible on the invoice PDF for the recipient.
                </p>
              </div>
            </div>
            <textarea
              className={styles.fieldTextarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Visible on the invoice PDF."
            />
          </div>

          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div>
                <h4 className={styles.panelTitle}>Payment Instructions</h4>
                <p className={styles.panelText}>
                  Offline settlement instructions such as bank transfer details.
                </p>
              </div>
            </div>
            <textarea
              className={styles.fieldTextarea}
              value={paymentInstructions}
              onChange={(e) => setPaymentInstructions(e.target.value)}
              placeholder="Bank transfer or offline payment instructions."
            />
          </div>

          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div>
                <h4 className={styles.panelTitle}>Internal Notes</h4>
                <p className={styles.panelText}>
                  Internal only. Useful for tracking promised payment terms or context.
                </p>
              </div>
            </div>
            <textarea
              className={styles.fieldTextarea}
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Only visible to admins."
            />
          </div>
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.actionButtonGhost}`}
            onClick={handlePreview}
            disabled={previewLoading || submitting}
          >
            {previewLoading ? "Previewing..." : "Preview"}
          </button>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
            onClick={() => saveDraft(false)}
            disabled={submitting}
          >
            {submitting
              ? editingInvoiceId
                ? "Saving..."
                : "Creating..."
              : editingInvoiceId
                ? "Save Draft Changes"
                : "Save Draft"}
          </button>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
            onClick={() => saveDraft(true)}
            disabled={submitting}
          >
            {submitting ? "Sending..." : editingInvoiceId ? "Save and Send" : "Create and Send"}
          </button>
        </div>
      </section>

      {previewModalOpen && preview && (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manual-invoice-preview-title"
          onClick={() => setPreviewModalOpen(false)}
        >
          <section className={styles.modalShell} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <div className={styles.surfaceEyebrow}>Preview</div>
                <h3 className={styles.surfaceTitle} id="manual-invoice-preview-title">
                  Invoice Snapshot
                </h3>
                <p className={styles.surfaceText}>
                  Validate recipient details, line items, and totals before saving
                  or sending.
                </p>
              </div>
              <div className={styles.modalHeaderActions}>
                <div className={styles.previewTotals}>
                  <div className={styles.summaryLabel}>Total</div>
                  <div className={styles.metricValue}>
                    {formatAmount(preview.pricing.total_amount_halala)}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.modalCloseButton}
                  aria-label="Close invoice preview"
                  onClick={() => setPreviewModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>

            <div className={styles.modalBody}>
              {preview.matched_user && !linkedUser && (
                <div className={styles.matchedBanner} style={{ padding: "0.8rem", marginBottom: "0.8rem" }}>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        color: "var(--Colors-Primary-800)",
                      }}
                    >
                      Existing C-Shaped user match found
                    </div>
                    <div className={styles.compactMeta} style={{ marginTop: "0.15rem" }}>
                      {preview.matched_user.full_name || preview.matched_user.email} ·{" "}
                      {preview.matched_user.email}
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.previewMetricGrid}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Subtotal</div>
                  <div className={styles.summaryValue}>
                    {formatAmount(preview.pricing.subtotal_amount_halala)}
                  </div>
                  <div className={styles.summaryHint}>
                    {preview.line_items.length} finalized line items
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Tax</div>
                  <div className={styles.summaryValue}>
                    {formatAmount(preview.pricing.tax_amount_halala)}
                  </div>
                  <div className={styles.summaryHint}>
                    {preview.pricing.tax_percentage}% applied to the subtotal
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Timeline</div>
                  <div className={styles.summaryValue}>{formatDate(preview.issue_date)}</div>
                  <div className={styles.summaryHint}>
                    {preview.due_date ? `Due ${formatDate(preview.due_date)}` : "No due date"}
                  </div>
                </div>
              </div>

              <div
                className={styles.summaryCard}
                style={{ marginBottom: "0.8rem", background: "var(--theme-surface, rgba(255, 255, 255, 0.75))" }}
              >
                <div className={styles.summaryLabel}>Recipient</div>
                <div className={styles.summaryValue}>
                  {preview.recipient.name} · {preview.recipient.email}
                </div>
                <div className={styles.summaryHint}>
                  {preview.recipient.company_name || "No company specified"}
                  {preview.recipient.city ? ` · ${preview.recipient.city}` : ""}
                  {preview.recipient.country ? ` · ${preview.recipient.country}` : ""}
                </div>
              </div>

              <div className={styles.previewItemList}>
                {preview.line_items.map((item, index) => (
                  <div
                    key={`${item.description}-${index}`}
                    className={styles.previewItemCard}
                    style={{ padding: "0.72rem 0.85rem" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "0.85rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            color: "var(--Colors-Primary-800)",
                          }}
                        >
                          {item.description}
                        </div>
                        <div className={styles.compactMeta} style={{ marginTop: "0.1rem" }}>
                          Qty {item.quantity}
                          {item.unit_label ? ` ${item.unit_label}` : ""} · Unit{" "}
                          {item.unit_amount_sar} SAR
                        </div>
                      </div>
                      <div
                        style={{
                          fontWeight: 800,
                          color: "var(--Colors-Primary-800)",
                        }}
                      >
                        {formatAmount(item.line_total_halala)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {(preview.notes || preview.payment_instructions || preview.internal_notes) && (
                <div className={styles.noteGrid} style={{ marginTop: "0.8rem" }}>
                  {preview.notes && (
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Notes</div>
                      <div className={styles.summaryHint}>{preview.notes}</div>
                    </div>
                  )}
                  {preview.payment_instructions && (
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Payment Instructions</div>
                      <div className={styles.summaryHint}>{preview.payment_instructions}</div>
                    </div>
                  )}
                  {preview.internal_notes && (
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Internal Notes</div>
                      <div className={styles.summaryHint}>{preview.internal_notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.actionButtonGhost}`}
                onClick={() => setPreviewModalOpen(false)}
              >
                Close Preview
              </button>
            </div>
          </section>
        </div>
      )}

      <section className={styles.historySurface}>
        <div className={styles.historyTop}>
          <div>
            <div className={styles.surfaceEyebrow}>History</div>
            <h3 className={styles.surfaceTitle}>Manual Invoice Ledger</h3>
            <p className={styles.surfaceText}>
              Drafts, sent invoices, manual payment confirmations, and branded PDF
              access all live here.
            </p>
          </div>
          <span className={styles.pill} style={statusBadge("draft")}>
            {invoices.length} total
          </span>
        </div>

        {listLoading ? (
          <div className={styles.emptyState}>Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className={styles.emptyState}>
            No manual invoices yet. Build a draft above to start the ledger.
          </div>
        ) : (
          <div className={styles.historyList}>
            {invoices.map((invoice) => {
              const busy = actionLoading?.startsWith(invoice.id);

              return (
                <article key={invoice.id} className={styles.historyCard} style={{ padding: "0.78rem" }}>
                  <div className={styles.historyTop}>
                    <div>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: "1.08rem",
                          color: "var(--Colors-Primary-800)",
                        }}
                      >
                        {invoice.invoice_number}
                      </div>
                      <div className={styles.compactMeta} style={{ marginTop: "0.2rem" }}>
                        {invoice.recipient_name} · {invoice.recipient_email}
                      </div>
                      <div className={styles.compactMeta} style={{ marginTop: "0.2rem" }}>
                        Created {formatDate(invoice.created_at)}
                      </div>
                    </div>
                    <div className={styles.actionGroup}>
                      <span className={styles.pill} style={statusBadge(invoice.status)}>
                        {invoice.status}
                      </span>
                      {invoice.is_overdue && (
                        <span className={styles.pill} style={statusBadge("overdue")}>
                          overdue
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.historyStatsGrid}>
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Total</div>
                      <div className={styles.summaryValue}>
                        {formatAmount(invoice.total_amount_halala)}
                      </div>
                      <div className={styles.summaryHint}>
                        {invoice.currency} · subtotal{" "}
                        {formatAmount(invoice.subtotal_amount_halala)}
                      </div>
                    </div>
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Issue / Due</div>
                      <div className={styles.summaryValue}>{formatDate(invoice.issue_date)}</div>
                      <div className={styles.summaryHint}>
                        {invoice.due_date ? `Due ${formatDate(invoice.due_date)}` : "No due date"}
                      </div>
                    </div>
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Email Sends</div>
                      <div className={styles.summaryValue}>
                        {invoice.invoice_email_send_count || 0}
                      </div>
                      <div className={styles.summaryHint}>
                        {invoice.invoice_email_sent_at
                          ? `Last sent ${formatDate(invoice.invoice_email_sent_at)}`
                          : "No email dispatch logged"}
                      </div>
                    </div>
                    <div className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>Linked User</div>
                      <div className={styles.summaryValue}>
                        {invoice.linked_user?.full_name || invoice.linked_user?.email || "None"}
                      </div>
                      <div className={styles.summaryHint}>
                        {invoice.linked_user?.email || "Standalone recipient record"}
                      </div>
                    </div>
                  </div>

                  <div className={styles.historyItemsList}>
                    {invoice.line_items.map((item) => (
                      <div key={item.id} className={styles.historyItem} style={{ padding: "0.62rem 0.72rem" }}>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              color: "var(--Colors-Primary-800)",
                            }}
                          >
                            {item.description}
                          </div>
                          <div className={styles.compactMeta} style={{ marginTop: "0.15rem" }}>
                            Qty {item.quantity}
                            {item.unit_label ? ` ${item.unit_label}` : ""} · Unit{" "}
                            {item.unit_amount_sar} SAR
                          </div>
                        </div>
                        <div
                          style={{
                            fontWeight: 800,
                            color: "var(--Colors-Primary-800)",
                          }}
                        >
                          {formatAmount(item.line_total_halala)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {(invoice.notes || invoice.payment_instructions || invoice.internal_notes) && (
                    <div className={styles.noteGrid}>
                      {invoice.notes && (
                        <div className={styles.summaryCard}>
                          <div className={styles.summaryLabel}>Notes</div>
                          <div className={styles.summaryHint}>{invoice.notes}</div>
                        </div>
                      )}
                      {invoice.payment_instructions && (
                        <div className={styles.summaryCard}>
                          <div className={styles.summaryLabel}>Payment Instructions</div>
                          <div className={styles.summaryHint}>
                            {invoice.payment_instructions}
                          </div>
                        </div>
                      )}
                      {invoice.internal_notes && (
                        <div className={styles.summaryCard}>
                          <div className={styles.summaryLabel}>Internal Notes</div>
                          <div className={styles.summaryHint}>{invoice.internal_notes}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {(invoice.status === "draft" || invoice.status === "sent") && (
                    <div className={styles.paymentGrid}>
                      <div>
                        <label style={label}>Payment Reference</label>
                        <input
                          className={styles.fieldInput}
                          value={paymentReferences[invoice.id] || ""}
                          onChange={(e) =>
                            setPaymentReferences((current) => ({
                              ...current,
                              [invoice.id]: e.target.value,
                            }))
                          }
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <label style={label}>Paid Date</label>
                        <input
                          className={styles.fieldInput}
                          type="date"
                          value={paymentDates[invoice.id] || ""}
                          onChange={(e) =>
                            setPaymentDates((current) => ({
                              ...current,
                              [invoice.id]: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                        onClick={() => markPaid(invoice.id)}
                        disabled={busy}
                      >
                        Mark Paid
                      </button>
                    </div>
                  )}

                  <div className={styles.actionGroup}>
                    {invoice.status === "draft" && (
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.actionButtonGhost}`}
                        onClick={() => loadDraft(invoice)}
                        disabled={busy}
                      >
                        Edit Draft
                      </button>
                    )}
                    {invoice.status === "draft" && (
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                        onClick={() => runInvoiceAction(invoice.id, "send")}
                        disabled={busy}
                      >
                        Send
                      </button>
                    )}
                    {invoice.status === "sent" && (
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.actionButtonGhost}`}
                        onClick={() => runInvoiceAction(invoice.id, "resend")}
                        disabled={busy}
                      >
                        Resend
                      </button>
                    )}
                    {(invoice.status === "draft" || invoice.status === "sent") && (
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                        onClick={() => runInvoiceAction(invoice.id, "cancel")}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                    )}
                    {invoice.pdf_path && (
                      <button
                        type="button"
                        className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                        onClick={() => openPdf(invoice.id)}
                        disabled={busy}
                      >
                        Open PDF
                      </button>
                    )}
                    {invoice.paid_at && (
                      <div className={styles.compactMeta}>
                        Paid: <strong>{formatDate(invoice.paid_at)}</strong>
                      </div>
                    )}
                    {invoice.payment_reference && (
                      <div className={styles.compactMeta}>
                        Reference: <strong>{invoice.payment_reference}</strong>
                      </div>
                    )}
                  </div>

                  {invoice.last_error && (
                    <div className={styles.errorBox} style={{ padding: "0.7rem 0.82rem" }}>
                      {invoice.last_error}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminManualInvoices;
